/**
 * Power BI deep-link URL builder for the survey results integration (ADR-077).
 *
 * Generates URLs like:
 *
 *   https://app.powerbi.com/groups/{workspaceId}/reports/{reportId}/{pageId}
 *     ?filter=Responses/SurveyId eq '{surveyId}'
 *
 * Page section IDs are auto-generated by the Power BI Service when pages
 * are created in the web editor. They do NOT match the display names —
 * Power BI renders the URL against the internal `id` of the page section,
 * not its label. Capture the IDs after authoring via
 * `npm run deploy:powerbi-report -- --print-page-ids` and update the
 * mapping below to match.
 *
 * Configuration via Vite env vars (set at build time):
 *   VITE_POWERBI_WORKSPACE_ID  — GUID of the Power BI workspace
 *   VITE_POWERBI_REPORT_ID     — GUID of the deployed Survey Results report
 *
 * Both are printed by `npm run deploy:powerbi-report` at the end of a
 * successful run. Paste into the admin .env for the target build.
 */
import type { SurveyType } from "@event-planner/shared";

/**
 * Page section IDs captured from the Power BI Service web editor URLs.
 * These are auto-generated when pages are created — update after any
 * report rebuild by navigating to each page and copying the ID from the URL.
 */
const POWERBI_PAGE_IDS: Record<SurveyType, string> = {
  rating: "703600cb45325adbc231",
  single_choice: "6c9994f8e0b125a3ee62",
  free_text: "33b2aa23ee027d204995",
};

export function isPowerBIConfigured(): boolean {
  return !!(
    import.meta.env.VITE_POWERBI_WORKSPACE_ID &&
    import.meta.env.VITE_POWERBI_REPORT_ID
  );
}

interface PowerBIDeepLinkInput {
  surveyType: SurveyType;
  surveyId: string;
}

/**
 * Builds a deep link to the Power BI report, opening on the page tailored
 * to the survey's type and pre-filtered to that survey's responses.
 *
 * Returns null if Power BI is not configured for this admin build.
 */
export function buildSurveyResultsPowerBILink({
  surveyType,
  surveyId,
}: PowerBIDeepLinkInput): string | null {
  const workspaceId = import.meta.env.VITE_POWERBI_WORKSPACE_ID;
  const reportId = import.meta.env.VITE_POWERBI_REPORT_ID;
  if (!workspaceId || !reportId) return null;

  const pageId = POWERBI_PAGE_IDS[surveyType] ?? POWERBI_PAGE_IDS.rating;
  const filterExpr = `Responses/SurveyId eq '${surveyId}'`;

  return (
    `https://app.powerbi.com/groups/${workspaceId}` +
    `/reports/${reportId}/${pageId}` +
    `?filter=${encodeURIComponent(filterExpr)}`
  );
}
